import moment from 'moment'
import { createChangeLog } from './changeLogActions'
const nodeURL = process.env.REACT_APP_STRAPI_URL
const headers = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("accessToken")}`
})

/** Work order id for activity log; schedule items without a WO (e.g. OFF) have no id. */
const getWorkorderIdForChangelog = (data) => {
  if (!data || !data.workorder) return null
  const w = data.workorder
  if (typeof w === 'object' && w !== null) return w.id ?? w._id ?? null
  return typeof w === 'string' || typeof w === 'number' ? w : null
}

const pickWorkorderId = (wo) => {
  if (wo == null || wo === '') return null
  if (typeof wo === 'object' && wo !== null) return wo.id ?? wo._id ?? null
  return wo
}

/**
 * Mongo/Strapi expects team_scheduled / users as ObjectId strings[], not { id } objects
 * (CastError: Cast to ObjectId failed for value "{ id: '...' }").
 * Strip full user objects to id only to avoid 413 payloads.
 */
const normalizeUserRelationList = (arr) => {
  if (!Array.isArray(arr)) return arr
  return arr
    .filter(Boolean)
    .map((u) => {
      if (typeof u === 'object' && u != null && u.id != null) return u.id
      if (typeof u === 'object' && u != null && u._id != null) return u._id
      return u
    })
}

const leanUsersForTeamChangeEmail = (arr) => {
  if (!Array.isArray(arr)) return arr
  return arr.filter(Boolean).map((u) => ({
    id: u.id,
    name: u.name,
    email: u.email,
  }))
}

export const stripHeavySchedulePayload = (details) => {
  if (!details || typeof details !== 'object') return details
  const d = { ...details }

  if (Array.isArray(d.team_scheduled)) {
    d.team_scheduled = normalizeUserRelationList(d.team_scheduled)
  }
  if (Array.isArray(d.users)) {
    d.users = normalizeUserRelationList(d.users)
  }

  const woId = pickWorkorderId(d.workorder)
  if (woId != null) {
    d.workorder = woId
  } else {
    delete d.workorder
  }

  if (d.eventType === '' || d.eventType == null) delete d.eventType

  delete d.workOrder

  if (d.teamChanged) {
    if (Array.isArray(d.addedMembers)) {
      d.addedMembers = leanUsersForTeamChangeEmail(d.addedMembers)
    }
    if (Array.isArray(d.removedMembers)) {
      d.removedMembers = leanUsersForTeamChangeEmail(d.removedMembers)
    }
    if (d.scheduleItem && typeof d.scheduleItem === 'object') {
      d.scheduleItem = stripHeavySchedulePayload(d.scheduleItem)
    }
  }

  return d
}

// ********** CREATE SCHEDULE ITEM  ********** //
export const createScheduleitem = async details => {

  if (details.workorder == '') delete details.workorder
  const body = stripHeavySchedulePayload(details)

  const opts = {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(body)
  };

  const response = await fetch(`${nodeURL}/scheduleitems`, opts);
  return response.json();
};

// ********** GET ALL SCHEDULE ITEMS  ********** //
export const fetchScheduleitems = async (date, view) => {
  let startDate, endDate
  if (view == 'timeGridDay') {
    startDate = moment(date).subtract(1, 'days').format('YYYY-MM-DD')
    endDate = moment(date).add(2, 'days').format('YYYY-MM-DD')
  } else if (view == 'timeGridWeek') {
    startDate = moment(date).subtract(10, 'days').format('YYYY-MM-DD')
    endDate = moment(date).add(10, 'days').format('YYYY-MM-DD')
  } else if (view == 'dayGridMonth') {
    startDate = moment(date).date(1).format('YYYY-MM-DD')
    endDate = moment(date).date(moment(date).daysInMonth()).add(1, 'days').format('YYYY-MM-DD')
  } else {
    startDate = moment(date).subtract(10, 'days').format('YYYY-MM-DD')
    endDate = moment(date).add(10, 'days').format('YYYY-MM-DD')
  }

  const opts = {
    method: 'GET',
    headers: headers()
  };

  const response = await fetch(`${nodeURL}/scheduleitems?_limit=9999&startTime_lte=${endDate}&endTime_gte=${startDate}`, opts);
  const data = await response.json();

  return data;
};

// ********** GET A SCHEDULE ITEM (BY ID)  ********** //
export const fetchScheduleitem = async id => {
  const opts = {
    method: 'GET',
    headers: headers()
  };

  const response = await fetch(`${nodeURL}/scheduleitems/${id}`, opts);
  const data = await response.json();
  return data;
};

// ********** UPDATE A SCHEDULE ITEM (BY ID)  ********** //
export const updateScheduleitem = async (id, details) => {

  if (details.workorder == '') delete details.workorder
  const body = stripHeavySchedulePayload(details)

  const opts = {
    method: 'PUT',
    headers: headers(),
    body: JSON.stringify(body)
  };

  const response = await fetch(`${nodeURL}/scheduleitems/${id}`, opts);
  const data = await response.json();

  if (response.ok && data.id) {
    const woId = getWorkorderIdForChangelog(data)
    if (woId != null) {
      await createChangeLog({
        description: `Schedule item updated. The updated item is on ` + moment(data.startTime).format('MMMM Do YYYY') + " from " + moment(data.startTime).format('h:mm:ss a') + " until " + moment(data.endTime).format('h:mm:ss a') + '.',
        user: JSON.parse(localStorage.getItem("user")).id,
        workorder: woId
      });
    }
  }

  return data;
};

// ********** DELETE A SCHEDULE ITEM (BY ID)  ********** //
export const deleteScheduleitem = async id => {
  const opts = {
    method: 'DELETE',
    headers: headers()
  };

  const response = await fetch(`${nodeURL}/scheduleitems/${id}`, opts);
  const data = await response.json();
  if (response.ok && data.id) {
    const woId = getWorkorderIdForChangelog(data)
    if (woId != null) {
      await createChangeLog({
        description: `Schedule item deleted. Scheduled item was on ` + moment(data.startTime).format('MMMM Do YYYY') + " from " + moment(data.startTime).format('h:mm:ss a') + " until " + moment(data.endTime).format('h:mm:ss a') + '.',
        user: JSON.parse(localStorage.getItem("user")).id,
        workorder: woId
      });
    }
  }

  return data;
};


export const searchScheduleItems = async (query, searchByTitle, searchByClient) => {
  const opts1 = {
    method: 'GET',
    headers: headers()
  };
  const opts2 = {
    method: 'POST',
    headers: headers()
  };

  if (searchByTitle && searchByClient) {
    const response1 = await fetch(`${nodeURL}/scheduleitems?title_contains=${query}&_sort=createdAt:DESC&_limit=9999`, opts1);
    const response2 = await fetch(`${nodeURL}/scheduleitems/byclientname?clientName=${query}&locationName=${query}`, opts2);
    const data1 = await response1.json();
    const data2 = await response2.json();

    return data1.concat(data2);
  }

  if (searchByTitle) {
    const response = await fetch(`${nodeURL}/scheduleitems?title_contains=${query}&_sort=createdAt:DESC&_limit=9999`, opts1);
    const data = await response.json();
    return data;
  }

  if (searchByClient) {
    const response = await fetch(`${nodeURL}/scheduleitems/byclientname?clientName=${query}&locationName=${query}`, opts2);
    const data = await response.json();
    return data;
  }
}